import { useQuery } from '@tanstack/react-query'
import { ArrowLeft } from 'lucide-react'
import { Link, useParams } from 'react-router-dom'

import { UsageMeter } from '@/components/subscription/UsageMeter'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { getDeveloperWorkspaceDetail } from '@/services/api/developer.service'
import { formatDateTime } from '@/utils/date'
import { formatLimitValue, formatUsageValue, numericLimit } from '@/utils/quota'

export function WorkspaceDetailPage() {
  const { workspaceId } = useParams<{ workspaceId: string }>()
  const { data, isLoading } = useQuery({
    queryKey: ['developer', 'workspace', 'detail', workspaceId],
    queryFn: () => getDeveloperWorkspaceDetail(workspaceId!),
    enabled: !!workspaceId,
  })

  if (isLoading) {
    return <div className="text-neutral-500">Loading workspace details...</div>
  }

  if (!data) {
    return <div className="text-neutral-500">Workspace not found.</div>
  }

  const usage = data.subscription?.entitlements ?? []

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="icon" asChild>
          <Link to="/developer/workspaces">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-3xl font-bold">{data.workspaceName}</h1>
          <p className="mt-1 text-neutral-500">Detailed workspace metrics and subscription state.</p>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-4">
        <Card><CardHeader><CardTitle>Members</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold">{data.memberCount}</div></CardContent></Card>
        <Card><CardHeader><CardTitle>Record Groups</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold">{data.recordGroupCount}</div></CardContent></Card>
        <Card><CardHeader><CardTitle>Records</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold">{data.recordCount}</div></CardContent></Card>
        <Card><CardHeader><CardTitle>Automations</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold">{data.automationCount}</div></CardContent></Card>
      </div>

      <div className="grid gap-6 xl:grid-cols-[2fr,1fr]">
        <Card>
          <CardHeader>
            <CardTitle>Subscription & Usage</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <Badge variant="outline">{data.subscription?.planName ?? 'No plan'}</Badge>
              <Badge variant="outline">{data.subscription?.status ?? 'NONE'}</Badge>
            </div>
            {usage.map((item) => (
              <UsageMeter
                key={item.featureKey}
                label={item.name}
                current={item.currentUsage}
                limit={numericLimit(item.limitValue)}
                formatValue={(value) => formatUsageValue(item.featureKey, value)}
                limitLabel={formatLimitValue(item.featureKey, item.limitValue)}
              />
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Locked Resources</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {data.lockedResources.length ? data.lockedResources.map((resource) => (
              <div key={resource.resourceId} className="rounded-lg border border-neutral-200 p-3">
                <p className="font-medium capitalize">{resource.resourceType}</p>
                <p className="text-xs text-neutral-500">{resource.resourceId}</p>
              </div>
            )) : <p className="text-sm text-neutral-500">No locked resources.</p>}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {data.recentActivities.length ? data.recentActivities.map((activity) => (
            <div key={String(activity.id)} className="rounded-lg border border-neutral-200 p-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-medium">{String(activity.actorName)}</p>
                  <p className="text-xs text-neutral-500">{String(activity.actionType)}</p>
                </div>
                <p className="text-xs text-neutral-400">{formatDateTime(String(activity.createdAt))}</p>
              </div>
              {activity.note ? <p className="mt-2 text-sm text-neutral-700">{String(activity.note)}</p> : null}
            </div>
          )) : <p className="text-sm text-neutral-500">No recent activity.</p>}
        </CardContent>
      </Card>
    </div>
  )
}
